import React, { useEffect, useMemo, useState } from "react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Label,
  Line,
  XAxis,
  YAxis,
} from "recharts";
import { GENERAL_METRICS_LABELS, METRICS_LABELS } from "constants/constants";
import { Tooltip, TooltipContent, TooltipTrigger } from "@tracify/ui/tooltip";
import { getNearestAxisValue } from "@components/ChannelControlMetrics/ConversionValueChart";
import { useAtom } from "jotai";
import { dashboardModeAtom } from "atoms";
import {
  useDateRangeState,
  useNewVsReturningState,
  type NvrType,
} from "@lib/hooks/url-state";
import useCurrency from "../../../lib/hooks/use-currency";
import useFormat from "@lib/hooks/use-format";
import XAxisTicksWithYear from "@components/Charts/XAxisTicksWithYear";
import { DailyMetric } from "@lib/api-hooks/useKpisOverview";
import {
  generalMetricsInformation,
  metricsInformation,
} from "@components/MetricNameWithTooltip/metricsInformation";
import InfoIcon from "@tracify/ui/info-icon";
import { Granularity } from "@lib/api-hooks/useKpiBreakdownForChannel";
import { formatGranularityTooltipLabel } from "@lib/util-functions";
import { alignComparisonDataByIndex } from "@lib/util-functions/comparisonBuckets";
import dayjs from "dayjs";
import { useIsMobile } from "@tracify/ui";
import { Card, CardContent, CardHeader } from "@tracify/ui/card";
import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartTooltip,
  ComparisonChartLegendContent,
  ComparisonChartTooltipContent,
} from "@tracify/ui/chart";

type Props = {
  historicalData: DailyMetric[];
  historicalComparedData: DailyMetric[];
  loading?: boolean;
  granularity?: Granularity;
};

const getChartData = ({
  historicalData,
  historicalComparedData,
  newVsReturning,
  granularity,
}: Props & { newVsReturning: NvrType; granularity: Granularity }) => {
  const dates = historicalData.map((el) => dayjs(el.date).unix());
  const comparedDates = historicalComparedData.map((el) =>
    dayjs(el.date).unix()
  );
  const minDateFromHistorical = Math.min(...dates);
  const minDateFromComparedHistorical = Math.min(...comparedDates);

  const unit = granularity === "monthly" ? "month" : "day";
  const timerange = dayjs(minDateFromHistorical * 1000).diff(
    dayjs(minDateFromComparedHistorical * 1000),
    unit
  );

  const currentChartData: { [key: string]: any }[] = [];
  let currentMaxValue = 0;
  let currentMaxMERValue = 0;

  // Populate with main data
  historicalData.forEach((el) => {
    const valueData: { [key: string]: any } = {};
    valueData["date"] = el.date;

    valueData["marketing_efficiency_ratio"] =
      el[newVsReturning].return_on_ad_spend ?? 0;
    valueData["cost_per_order"] = el[newVsReturning].cost_per_order ?? 0;
    valueData["customer_acquisition_cost"] =
      el[newVsReturning].customer_acquisition_cost ?? 0;
    if (el[newVsReturning].cost_per_order > currentMaxValue)
      currentMaxValue = el[newVsReturning].cost_per_order;
    if (el[newVsReturning].customer_acquisition_cost > currentMaxValue)
      currentMaxValue = el[newVsReturning].customer_acquisition_cost;
    if (el[newVsReturning].return_on_ad_spend > currentMaxMERValue)
      currentMaxMERValue = el[newVsReturning].return_on_ad_spend;
    currentChartData.push(valueData);
  });

  historicalComparedData.forEach((el) => {
    const date = dayjs(el.date).add(timerange, unit).format("YYYY-MM-DD");
    const existingElement = currentChartData.find((item) => item.date === date);
    if (existingElement) {
      existingElement.compared_marketing_efficiency_ratio =
        el[newVsReturning].return_on_ad_spend ?? 0;
      existingElement.compared_cost_per_order =
        el[newVsReturning].cost_per_order ?? 0;
      existingElement.compared_customer_acquisition_cost =
        el[newVsReturning].customer_acquisition_cost ?? 0;
    } else {
      const valueData: { [key: string]: any } = {};
      valueData["date"] = date;
      valueData["compared_marketing_efficiency_ratio"] =
        el[newVsReturning].return_on_ad_spend;
      valueData["compared_cost_per_order"] =
        el[newVsReturning].cost_per_order ?? 0;
      valueData["compared_customer_acquisition_cost"] =
        el[newVsReturning].customer_acquisition_cost ?? 0;
      currentChartData.push(valueData);
    }
  });

  return {
    chartData: currentChartData.sort((a, b) =>
      dayjs(a.date).diff(dayjs(b.date))
    ),
    maxMERValue: currentMaxMERValue,
    maxValue: currentMaxValue,
  };
};

/**
 * Component representing the charts in store performance sector.
 *
 * @param {CacDevelopmentPropsInterface} props
 * @return {CustomElements}
 */
export function CACDevelopmentChart({
  historicalData,
  historicalComparedData,
  loading,
  granularity = "daily",
}: Props) {
  const { newVsReturning } = useNewVsReturningState();
  const [dashboardMode] = useAtom(dashboardModeAtom);
  const { compared, startTime, endTime } = useDateRangeState();
  const { calculateCurrencyConvertion, globalCurrencySymbol } = useCurrency();
  const { formatNumber } = useFormat();

  const { chartData, maxMERValue, maxValue } = getChartData({
    historicalData: historicalData,
    historicalComparedData,
    newVsReturning,
    granularity,
  });

  const maxMERDomain = getNearestAxisValue(maxMERValue);
  const maxYDomain = getNearestAxisValue(maxValue);

  const isMobile = useIsMobile();
  const [xAxisTicks, setXAxisTicks] = useState(
    historicalData?.length ? historicalData.map((el) => el.date) : []
  );

  // Build chart config dynamically
  const chartConfig = useMemo<ChartConfig>(() => {
    const cpoLabel = dashboardMode === "general" ? "CPA" : "CPO";
    return {
      marketing_efficiency_ratio: {
        label: "MER",
        color: "#DAA702",
      },
      compared_marketing_efficiency_ratio: {
        label: "Previous MER",
        color: "#DAA702",
      },
      cost_per_order: {
        label:
          dashboardMode === "general"
            ? GENERAL_METRICS_LABELS["cost_per_order"] || cpoLabel
            : METRICS_LABELS["cost_per_order"] || cpoLabel,
        color: "#4a3fcc",
      },
      compared_cost_per_order: {
        label: `Previous ${cpoLabel}`,
        color: "#4a3fcc",
      },
      customer_acquisition_cost: {
        label:
          dashboardMode === "general"
            ? GENERAL_METRICS_LABELS["customer_acquisition_cost"] || "CAC"
            : METRICS_LABELS["customer_acquisition_cost"] || "CAC",
        color: "#27ae60",
      },
      compared_customer_acquisition_cost: {
        label: "Previous CAC",
        color: "#27ae60",
      },
    };
  }, [dashboardMode]);

  useEffect(() => {
    function setXAxisTicksHandler() {
      const revenueChart = document.getElementById("cac-chart-container");
      const width = revenueChart?.clientWidth ?? 0;
      // Adjust calculation for mobile - less space available
      const minWidth = isMobile ? 200 : 300;
      const maxAmountOfTicks = Math.floor((width - minWidth) / (36 + 15)); // the labels are roughly 36px wide + a 15px spacing
      const dateKeys = historicalData.map((el) => el.date);
      const dateKeysCount = dateKeys.length;
      const keysIndexToChoose = Math.ceil(dateKeysCount / maxAmountOfTicks);
      const keysToDisplay = dateKeys
        .map((el, index) => (index % keysIndexToChoose === 0 ? el : null))
        .filter((el) => Boolean(el)) as string[];
      setXAxisTicks(keysToDisplay);
    }
    setXAxisTicksHandler();
    window.addEventListener("resize", setXAxisTicksHandler);
    return () => {
      window.removeEventListener("resize", setXAxisTicksHandler);
    };
  }, [historicalData, isMobile]);

  const formatTooltipLabel = (label: string | number | undefined) => {
    if (!label) {
      return "";
    }

    const labelValue = String(label);
    const firstDate = chartData[0]?.date;
    const lastDate = chartData[chartData.length - 1]?.date;

    return formatGranularityTooltipLabel({
      date: labelValue,
      granularity,
      isFirstTick: labelValue === firstDate,
      isLastTick: labelValue === lastDate,
      startDate: startTime,
      endDate: endTime,
    });
  };

  // Custom value formatter for the tooltip
  const formatValue = (value: number, key: string) => {
    const baseKey = key.replace("compared_", "");
    if (baseKey === "marketing_efficiency_ratio") {
      return formatNumber(value, 2);
    }
    return calculateCurrencyConvertion(value, 2);
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <div className="h-7 w-48 rounded-lg bg-muted animate-pulse" />
        </CardHeader>
        <CardContent>
          <div className="w-full h-[250px] lg:h-[350px] animate-pulse bg-muted rounded-lg"></div>
        </CardContent>
      </Card>
    );
  }
  const chartHeight = isMobile ? 250 : 350;
  const chartMargin = isMobile
    ? { top: 20, bottom: 5, right: 0, left: 0 }
    : { top: 30, bottom: 5, right: 0, left: 0 };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center">
          <h3 className="h3 mr-3 ">
            CAC & {dashboardMode === "general" ? "CPA" : "CPO"} vs. MER Trend
          </h3>
          <Tooltip>
            <TooltipTrigger>
              <InfoIcon />
            </TooltipTrigger>
            <TooltipContent>
              <p className="max-w-lg">
                {dashboardMode === "general"
                  ? generalMetricsInformation.cpovsmer
                  : metricsInformation.cpovsmer}
              </p>
            </TooltipContent>
          </Tooltip>
        </div>
      </CardHeader>

      <CardContent>
        <ChartContainer
          config={chartConfig}
          className={`w-full ${isMobile ? "h-[300px]" : "h-[400px]"}`}
          id="cac-chart-container"
        >
          <ComposedChart
            id="cac-chart"
            data={chartData}
            margin={chartMargin}
            height={chartHeight}
          >
            <defs>
              <linearGradient
                id="gradient-customer_acquisition_cost"
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop
                  offset="5%"
                  stopColor="var(--color-customer_acquisition_cost)"
                  stopOpacity={compared ? 0.2 : 0.4}
                />
                <stop
                  offset="95%"
                  stopColor="var(--color-customer_acquisition_cost)"
                  stopOpacity={0}
                />
              </linearGradient>
              <linearGradient
                id="gradient-cost_per_order"
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop
                  offset="5%"
                  stopColor="var(--color-cost_per_order)"
                  stopOpacity={compared ? 0.2 : 0.4}
                />
                <stop
                  offset="95%"
                  stopColor="var(--color-cost_per_order)"
                  stopOpacity={0}
                />
              </linearGradient>
            </defs>

            <Area
              isAnimationActive={false}
              type="monotone"
              dataKey="customer_acquisition_cost"
              yAxisId="1"
              stroke="none"
              dot={false}
              fillOpacity={1}
              fill="url(#gradient-customer_acquisition_cost)"
              connectNulls
              legendType="none"
              tooltipType="none"
            />
            <Area
              isAnimationActive={false}
              type="monotone"
              dataKey="cost_per_order"
              stroke="none"
              yAxisId="1"
              dot={false}
              fillOpacity={1}
              fill="url(#gradient-cost_per_order)"
              connectNulls
              legendType="none"
              tooltipType="none"
            />
            <XAxis
              dataKey="date"
              tickSize={0}
              tickMargin={20}
              axisLine={false}
              textAnchor="start"
              interval={0}
              tick={(props) => (
                <XAxisTicksWithYear
                  rechartsProps={props}
                  xAxisTicks={xAxisTicks}
                  startDate={startTime}
                  endDate={endTime}
                  granularity={granularity}
                />
              )}
              ticks={xAxisTicks}
            />
            <CartesianGrid stroke="var(--chart-grid)" vertical={false} />
            <YAxis
              hide={isMobile}
              tickFormatter={(value: string) =>
                `${calculateCurrencyConvertion(
                  parseFloat(value.toString()),
                  0,
                  false
                )}`
              }
              tickSize={0}
              tickLine={false}
              yAxisId="1"
              tickMargin={10}
              domain={[0, maxYDomain]}
              axisLine={false}
              ticks={[0, 1, 2, 3, 4].map((el) => (maxYDomain / 4) * el)}
              width={isMobile ? 60 : 80}
            >
              <Label
                value={`${
                  dashboardMode === "general" ? "CPA" : "CPO"
                } / CAC in ${globalCurrencySymbol}`}
                offset={isMobile ? 10 : -10}
                angle={-90}
                textAnchor="middle"
                position={{
                  x: isMobile ? 10 : 20,
                  y: 60,
                }}
                style={{ fontSize: isMobile ? "11px" : "12px" }}
              />
            </YAxis>
            <YAxis
              hide={isMobile}
              orientation="right"
              yAxisId="2"
              label={{
                value: "MER",
                angle: -90,
                position: "insideRight",
                offset: isMobile ? 5 : 10,
              }}
              tickMargin={10}
              tickLine={false}
              domain={[0, maxMERDomain]}
              tickCount={
                maxMERDomain % 4 === 0 ? 5 : maxMERDomain % 2 === 0 ? 3 : 2
              }
              axisLine={false}
              width={isMobile ? 40 : 60}
            />

            <ChartTooltip
              position={{ y: 50 }}
              content={(props) => (
                <ComparisonChartTooltipContent
                  payload={props.payload}
                  label={props.label}
                  config={chartConfig}
                  isCompared={compared}
                  sortOrder={[
                    "customer_acquisition_cost",
                    "cost_per_order",
                    "marketing_efficiency_ratio",
                  ]}
                  labelFormatter={formatTooltipLabel}
                  valueFormatter={(v, key) => formatValue(v, key || "")}
                />
              )}
            />

            <Line
              isAnimationActive={false}
              type="monotone"
              dataKey="customer_acquisition_cost"
              yAxisId="1"
              stroke="var(--color-customer_acquisition_cost)"
              dot={false}
              strokeWidth={2}
              connectNulls
            />
            {compared ? (
              <Line
                type="monotone"
                yAxisId="1"
                dataKey="compared_customer_acquisition_cost"
                stroke="var(--color-customer_acquisition_cost)"
                strokeDasharray="5 5"
                opacity={0.75}
                dot={false}
                strokeWidth={2}
                connectNulls
              />
            ) : null}
            <Line
              isAnimationActive={false}
              type="monotone"
              dataKey="cost_per_order"
              stroke="var(--color-cost_per_order)"
              yAxisId="1"
              dot={false}
              strokeWidth={2}
              connectNulls
            />
            {compared ? (
              <Line
                type="monotone"
                yAxisId="1"
                dataKey="compared_cost_per_order"
                stroke="var(--color-cost_per_order)"
                strokeDasharray="5 5"
                opacity={0.75}
                dot={false}
                strokeWidth={2}
                connectNulls
              />
            ) : null}
            <Line
              type="monotone"
              yAxisId="2"
              dataKey="marketing_efficiency_ratio"
              stroke="var(--color-marketing_efficiency_ratio)"
              dot={false}
              strokeWidth={2}
            />
            {compared ? (
              <Line
                type="monotone"
                yAxisId="2"
                dataKey="compared_marketing_efficiency_ratio"
                stroke="var(--color-marketing_efficiency_ratio)"
                strokeDasharray="5 5"
                opacity={0.75}
                dot={false}
                strokeWidth={2}
                connectNulls
              />
            ) : null}

            <ChartLegend
              content={(props) => (
                <ComparisonChartLegendContent
                  payload={props.payload}
                  config={chartConfig}
                  isCompared={compared}
                  sortOrder={[
                    "customer_acquisition_cost",
                    "cost_per_order",
                    "marketing_efficiency_ratio",
                  ]}
                />
              )}
            />
          </ComposedChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
